import React, { Fragment } from 'react'
import UseCase1Frame1 from 'src/components/use-case-1-frame-1'
import UseCase2Frame1 from 'src/components/use-case-2-frame-1'
import UseCase3Frame1 from 'src/components/use-case-3-frame-1'
import UseCase4Frame1 from 'src/components/use-case-4-frame-1'
import UseCase5Frame1 from 'src/components/use-case-5-frame-1'
import UseCase6Frame1 from 'src/components/use-case-6-frame-1'
import UseCase7Frame1 from 'src/components/use-case-7-frame-1'
import UseCase8Frame1 from 'src/components/use-case-8-frame-1'

export default [
  {
    id: 'use-case-1',
    buttonLabel: 'Improve mobile conversion rates',
    frames: UseCase1Frame1,
    introCopy: (
      <p>
        It's a fact: most of your traffic is mobile. When a mobile user clicks your partners' links, are they
        going to mobile web? Your app? The app store? If you want to see better mobile conversion rates, you
        need a better mobile user experience.
      </p>
    ),
    description: (
      <p>
        If the user already has your app installed, it's a no-brainer to send them into your app whenever they
        click one of your links on the web. But what if they don't have your app? Let the user choose whether
        to open the page in mobile web or download your app. And once they download your app, take them
        directly to the product they clicked on as soon as they open your app for the first time.
      </p>
    ),
    content1: {
      copy1: "Mobile user clicks partner's link.",
      copy2: 'Is app installed?',
      button1: 'Yes',
      button2: 'No',
    },
    content2: {
      copy1: 'Great!',
      copy2: 'User opens the app and arrives directly on the requested product page.',
      button1: 'Start over',
    },
    content3: {
      copy1: 'Give user a choice: Open in-app or mobile web?',
      button1: 'App',
      button2: 'Web',
      button3: 'Back',
    },
    content4: {
      copy1: 'App',
      copy2: 'TrueLink opens app store listing.',
      button1: 'User installs app',
      button2: 'Back',
    },
    content5: {
      copy1: 'Success!',
      copy2: 'TrueLink opens requested product page in mobile web.',
      button1: 'Start over',
    },
  },
  {
    id: 'use-case-2',
    buttonLabel: 'Outdated offers',
    frames: UseCase2Frame1,
    introCopy: (
      <Fragment>
        <p>
          You want a wide variety of partners promoting your offer — but what if they don't update when you
          change it? After all, you don't have any direct control over how quickly they swap out the link on
          their site.
        </p>
        <p>
          Oh wait. Yes, you do. With TrueLink, you can simply mark an offer or link as no longer valid. So
          even when customers click that outdated link, you can show them a message that the offer is no
          longer available or simply redirect them to the current offer.
        </p>
      </Fragment>
    ),
    description: (
      <p>
        Here's a scenario: You're promoting a credit card. The interest rate has just gone up, but your
        partner is still promoting the old interest rate. Normally, this would be a regulatory nightmare. With
        TrueLink, you can interject a message before the user lands on your site, and even require them to
        acknowledge that the offer has changed. Then you can simply show them the new offer!
      </p>
    ),
    content1: {
      copy1: "A user clicks partner's link.",
      copy2: 'Is the offer valid?',
      button1: 'Yes', //content-2
      button2: 'No', //content-3
    },
    content2: {
      copy1: 'Great!',
      copy2: 'Great! TrueLink takes them to the offer.',
      button1: 'Start over', //content-1
    },
    content3: {
      copy1: 'Uh oh!',
      copy2: 'Truelink sends you to interstitial page that says "This offer is no longer available."',
      button1: 'See new offer', //content-4
      button2: 'Back', //content-2
    },
    content4: {
      copy1: 'Success!',
      copy2: 'Truelink redirects user and sends them to the most current offer.',
      button1: 'Start over', //content-1
    },
  },
  {
    id: 'use-case-3',
    buttonLabel: 'Location-based promos',
    frames: UseCase3Frame1,
    introCopy: (
      <p>
        If you're looking to drive conversions in a specific location — say, New York — the natural thing to
        do is offer a location-specific discount. But this is the internet. "Meg" in New York can forward the
        offer to "Tom" in Seattle, and before you know it, people all over the world are using the deal you
        intended just for New Yorkers. Let's fix that.
      </p>
    ),
    description: (
      <p>
        Since we can see the IP address of anyone clicking on a partner's link, TrueLink can automatically
        decide whether to route a user to a location-specific page. So rather than provide a special (and
        easily transferrable) code to the people you think are in New York, you can just set up a separate
        landing page and route anyone with a New York IP address to that page. Oh, and you can do it with
        in-app locations as easily as web pages.
      </p>
    ),
    content1: {
      copy1: "Mobile user clicks partner's link.",
      copy2: 'Is app installed?',
      button1: 'Yes',
      button2: 'No',
    },
    content2: {
      copy1: 'Is the user in New York?',
      button1: 'Yes',
      button2: 'No',
      button3: 'Back',
    },
    content3: {
      copy1: 'Great! Is the user in New York?',
      button1: 'Yes',
      button2: 'No',
      button3: 'Back',
    },
    content4: {
      copy1: 'Success!',
      copy2: 'User has been directed to the default promo in-app location',
      button1: 'Start over',
    },
    content5: {
      copy1: 'Success!',
      copy2: 'User has been redirected to the New York promo in-app location',
      button1: 'Start over',
    },
    content6: {
      copy1: 'Success!',
      copy2: 'User has been redirected to the New York promo in the mobile browser',
      button1: 'Start over',
    },
    content7: {
      copy1: 'Success!',
      copy2: 'User has been directed to the default promo in the mobile browser',
      button1: 'Start over',
    },
  },
  {
    id: 'use-case-4',
    buttonLabel: 'Instant loyalty partnerships',
    frames: UseCase4Frame1,
    introCopy: (
      <Fragment>
        <p>
          Let's say you want to work with an airline. They'll promote your products within their loyalty
          portal, and you'll give their users free miles when they purchase. How do you make it happen? Well,
          you could kick off a complicated integration and tracking process that requires input from several
          teams on both sides. Or you could pay the airline cash and rely on them to correctly convert to
          miles and deposit the right amount into the right account. Or (best answer!), you could use
          TrueLink.
        </p>
      </Fragment>
    ),
    description: (
      <p>
        How does TrueLink help here? Once we see that a given user has clicked on the airline's link and is
        headed to your site, we can reroute them to an "interstitial" page. Essentially, we'll show them a
        popup that asks for their frequent-flier number. If they enter a valid number, we can connect it to
        the eventual conversion and pass all that data back to you. You can then pass it to the airline, and
        they'll know exactly who (and how much) to credit.
      </p>
    ),
    content1: {
      copy1: "User clicks partner's link.",
      copy2: 'Where is the user coming from?',
      button1: 'Airline partner', // content3
      button2: 'Other partner', // content2
    },
    content2: {
      copy1: 'Success!',
      copy2: 'TrueLink redirects user to the requested landing page.',
      button1: 'Start over', // content1
    },
    content3: {
      copy1: 'User submits their frequent-flier number.',
      button1: 'Submit', // content4
      button2: 'Back', // content1
    },
    content4: {
      copy1: 'Success!',
      copy2: 'The user has been notified that and is redirected to the booking page.',
      button1: 'Start over', // content1
    },
    content5: {
      copy1: 'Success!',
      copy2:
        'The user has been notified that their points will be credited once they book their stay, and TrueLink redirects them to the booking page.',
      button1: 'Start over', // content1
    },
  },
  {
    id: 'use-case-5',
    buttonLabel: 'A/B testing: landing pages',
    frames: UseCase5Frame1,
    introCopy: (
      <Fragment>
        <p>
          A/B testing is a great way to optimize your conversions rates. By sending half of your users to one
          landing page and half to another, you can learn which page is better at convincing users to convert.
        </p>
        <p>
          With TrueLink, you can test in both mobile web and your app. Plus, you can layer in extra
          conditionals — for example, you can test certain landing pages only if the traffic is coming from a
          specific partner.
        </p>
      </Fragment>
    ),
    description: (
      <p>
        Here's how. First, TrueLink identifies whether the user has your app installed. Then, it determines
        whether the user is headed to the page you're trying to test. If not, then TrueLink simply lets the
        user go wherever they were going. If they are going to the product page you want to test, TrueLink
        will randomly assign the user to the test or control group, and reroute them to the appropriate page
        or in-app location.
      </p>
    ),
    content1: {
      copy1: "Mobile user clicks partner's link.",
      copy2: 'Is app installed?',
      button1: 'Yes', // content3
      button2: 'No', // content2
    },
    content2: {
      copy1: 'Where does the link take them to?',
      button1: 'Product', // content4
      button2: 'Home', // content5
      button3: 'Back',
    },
    content3: {
      copy1: 'Where does the link take them to?',
      button1: 'Product', // content7
      button2: 'Home', // content6
      button3: 'Back', // content1
    },
    content4: {
      copy1: 'Success!',
      copy2: 'User is sent to the home page on the mobile web.',
      button1: 'Start over', // content1
    },
    content5: {
      copy1: 'Success!',
      copy2: 'Send 50% to landing page A and 50% to landing page B (mobile web).',
      button1: 'Start over', // content1
    },
    content6: {
      copy1: 'Success!',
      copy2: "User is sent to the app's home page.",
      button1: 'Start over', // content1
    },
    content7: {
      copy1: 'Success!',
      copy2: 'Send 50% to landing page A and 50% to landing page B (in-app).',
      button1: 'Start over', // content1
    },
  },
  {
    id: 'use-case-6',
    buttonLabel: 'A/B testing: mobile UX',
    frames: UseCase6Frame1,
    introCopy: (
      <Fragment>
        <p>
          We've already looked at A/B testing to optimize your landing pages, but now let's take a step back
          and optimize the entire user experience (UX).
        </p>

        <p>
          In this scenario, we can still test landing pages, but we can also find out whether it's better to
          send users to mobile web or to the app store.
        </p>
      </Fragment>
    ),
    description: (
      <p>
        We have a couple of options now: test different landing pages as before, or test the overall user flow
        between mobile web and apps. It's more complicated, but TrueLink can help. Special tip: To see the
        whole picture, be sure to take into account not only conversions, but also the new app users you get
        from each approach.
      </p>
    ),
    content1: {
      copy1: "Mobile user clicks partner's link.",
      copy2: 'What do you want to test?',
      button1: 'Landing page',
      button2: 'Mobile UX',
    },
    content2: {
      copy1: 'Success!',
      copy2:
        'TrueLink sends 50% to the default landing page, while the other 50% have been sent to the test landing page.',
      button1: 'Start over',
    },
    content3: {
      copy1: 'Success!',
      copy2:
        'TrueLink sends 50% of users to the default landing page, while the other 50% have been sent to the app store.',
      button1: 'Start over',
    },
  },
  {
    id: 'use-case-7',
    buttonLabel: 'Personalization',
    frames: UseCase7Frame1,
    introCopy: (
      <p>
        Some partners — especially sites such as loyalty portals that require users to log in — have a great
        deal of data on their users' shopping habits. If your partner knows which categories their users tend
        to purchase, you can use TrueLink to quickly direct them to the place they're most likely to convert.
      </p>
    ),
    description: (
      <p>
        For example, let's say a user clicks your partner's link to your home page. If they don't have any
        data on that user, then TrueLink will just send the user to your home page as expected. However, if
        they happen to know that the user frequently shops for, say, sporting goods or beauty products,
        TrueLink can absorb that data and dynamically send the user to a specific category page instead.
      </p>
    ),
    content1: {
      copy1: "User clicks Partner A's link.",
      copy2: 'Does BackCash Rewards recognize the user?',
      button1: 'Yes', // 2
      button2: 'No', // 5
    },
    content2: {
      // loader
      copy1: 'Which product category do they frequently shop?',
      button1: 'Sporting goods', // 3
      button2: 'Beauty', // 4
      button3: 'Back', // 1
    },
    content3: {
      copy1: 'Success!',
      copy2: 'TrueLink routes user to the sporting goods category page.',
      button1: 'Start over', // 1
    },
    content4: {
      copy1: 'Success!',
      copy2: 'TrueLink routes user to the beauty category page.',
      button1: 'Start over', // 1
    },
    content5: {
      copy1: "That's fine!", // 1
      copy2: 'TrueLink takes user to the generic home page.',
      button1: 'Start over',
    },
  },
  {
    id: 'use-case-8',
    buttonLabel: 'Time-sensitive promos',
    frames: UseCase8Frame1,
    introCopy: (
      <p>
        We've all been there: A major sale has come and gone, but a few partners are still hosting links to
        pages of now-outdated deals. You don't want to replace the page entirely, but you also don't want your
        customers to land there. Sounds like a job for TrueLink!
      </p>
    ),
    description: (
      <p>
        With TrueLink, you can set date- and time-based rules for any URL. So for example, as long as it's
        still the day after Thanksgiving, any link to the Black Friday page will work just fine. But
        afterward, you can automatically route traffic elsewhere. This prevents your customers from feeling
        like they missed out, and gives you a good way to highlight the deals they still can take advantage of
        — all without relying on your partners to swap any URLs.
      </p>
    ),
    content1: {
      copy1: 'User clicks a link marked as "Black Friday."',
      copy2: 'Is it still Black Friday?',
      button1: 'Yes', // 2
      button2: 'No', // 3
    },
    content2: {
      copy1: 'Carry on then.',
      copy2: 'TrueLink sends user to the Black Friday page.',
      button1: 'Start over', // 1
    },
    content3: {
      // loader
      copy1: 'Is it now Cyber Monday?',
      button1: 'Yes', // 4
      button2: 'No', // 5
      button3: 'Back',
    },
    content4: {
      copy1: 'Success!',
      copy2: 'TrueLink redirect user to the Cyber Monday page.',
      button1: 'Start over', // 1
    },
    content5: {
      copy1: "No, it's December.",
      copy2:
        'No one wants to see deals they missed out on. TrueLink redirects the user to a generic deals page.',
      button1: 'Start over', // 1
    },
  },
]
